/** Locales the app ships. Content is stored one row per (card, locale). */
export type Locale = 'en' | 'es';
export const LOCALES: readonly Locale[] = ['en', 'es'] as const;

/**
 * Suit ids follow the image folder names in `img/`, which is also what
 * `tarot-data.js` uses. "pentacles" is displayed as "Coins" in EN and
 * "Oros" in ES, but the id never changes.
 */
export type Suit = 'major' | 'wands' | 'cups' | 'swords' | 'pentacles';

/** Standard reading order, per SPEC: Major, Wands, Cups, Swords, Coins. */
export const SUIT_ORDER: readonly Suit[] = [
  'major',
  'wands',
  'cups',
  'swords',
  'pentacles',
] as const;

export type Arcana = 'major' | 'minor';

/** Canonical card id, e.g. "major-0", "cups-3". Deck-agnostic on purpose. */
export type CardId = string;

/**
 * A semantic card. Deliberately carries NO artwork and NO prose: art lives on
 * DeckCard, prose lives on CardContent. That separation is what lets an
 * "Art Nouveau" deck reuse these exact 78 cards with different images.
 */
export interface Card {
  id: CardId;
  arcana: Arcana;
  suit: Suit;
  /** 0-21 for major; 1-14 for minor (11=Page, 12=Knight, 13=Queen, 14=King). */
  number: number;
  /** Court rank for minor cards, null for pips and major arcana. */
  rank: CourtRank | null;
  tags: string[];
}

export type CourtRank = 'page' | 'knight' | 'queen' | 'king';

/** Where a piece of content came from, so curated text is never overwritten. */
export type ContentSource = 'curated' | 'derived' | 'ai';

/** The five meanings from the SPEC, per card per locale. */
export interface CardContent {
  cardId: CardId;
  locale: Locale;
  name: string;
  keywords: string[];
  /** Free tier. Condensed from the curated long-form meaning. */
  full: string;
  /** Free tier. */
  reversed: string;
  /** Paid tier — the complete 20-years-of-insight text. */
  inDepth: string;
  source: ContentSource;
  /**
   * Set when `full`/`inDepth` are serving another locale's text because this
   * locale has no translation yet. The UI surfaces this rather than silently
   * showing English to a Spanish reader.
   */
  fallbackLocale?: Locale;
}

/** A card as the UI consumes it: semantics + this deck's art + this locale's prose. */
export interface CardView extends Card {
  content: CardContent;
  artPath: string;
  sortOrder: number;
  deckSlug: string;
}

export type DeckType =
  | 'tarot78'
  | 'art-variant'
  | 'subset'
  | 'superset'
  | 'oracle';

export type DeckStatus = 'available' | 'coming-soon';

export interface Deck {
  slug: string;
  type: DeckType;
  /** Art variants borrow meanings from their base deck's cards. */
  baseDeckSlug: string | null;
  name: Record<Locale, string>;
  blurb: Record<Locale, string>;
  status: DeckStatus;
  /** Lowest tier that may open this deck. */
  minTier: Tier;
  coverImage: string;
  cardBackImage: string;
}

export type RelationType = 'equivalent' | 'see-also' | 'alternate-art';

// ── Users, tiers, prefs ──────────────────────────────────────────────

/**
 * Access tiers. `gifted`, `mystic` and `oracle` all count as "paid" for
 * feature purposes; they differ only in how they are granted and billed.
 */
export type Tier = 'guest' | 'free' | 'mystic' | 'oracle' | 'gifted';

export type ReversalFrequency = 'very-low' | 'low' | 'natural';

/** Odds of any one card landing reversed, per SPEC. */
export const REVERSAL_ODDS: Record<ReversalFrequency, number> = {
  'very-low': 1 / 8,
  low: 1 / 4,
  natural: 1 / 2,
};

/**
 * The twelve tropical signs, in their calendar order starting from the
 * vernal equinox. Their date ranges and display data live in `zodiac.ts`;
 * the type and default stay here alongside the rest of the tier/prefs
 * primitives so `UserPrefs` does not need a circular import.
 */
export type ZodiacSign =
  | 'aries'
  | 'taurus'
  | 'gemini'
  | 'cancer'
  | 'leo'
  | 'virgo'
  | 'libra'
  | 'scorpio'
  | 'sagittarius'
  | 'capricorn'
  | 'aquarius'
  | 'pisces';

export const ZODIAC_SIGN_ORDER: readonly ZodiacSign[] = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
] as const;

export const DEFAULT_ZODIAC_SIGN: ZodiacSign = 'aries';

export interface UserPrefs {
  locale: Locale;
  defaultDeck: string;
  /** Paid users may switch reversals off entirely. Free users may not. */
  reversalsEnabled: boolean;
  reversalFrequency: ReversalFrequency;
  /** Opt-in, off by default (SPEC: data collection). */
  shareInsights: boolean;
  /** Skip the "what's on your mind" step and draw straight away. */
  quickDraw: boolean;
  /** Which sign's daily horoscope the Horoscope tab shows. */
  zodiacSign: ZodiacSign;
}

export const DEFAULT_PREFS: UserPrefs = {
  locale: 'en',
  defaultDeck: 'rws',
  reversalsEnabled: true,
  reversalFrequency: 'low',
  shareInsights: false,
  quickDraw: false,
  zodiacSign: DEFAULT_ZODIAC_SIGN,
};

export interface PublicUser {
  id: string;
  email: string;
  tier: Tier;
  /** Operator role; unlocks the Admin tab. The API re-checks on every call. */
  isAdmin: boolean;
  prefs: UserPrefs;
  subscriptionStatus: SubscriptionStatus | null;
  /** Current calendar month's AI usage, so the UI can show a budget meter. */
  usage: { tokens: number; budget: number } | null;
  createdAt: string;
}

export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'trialing'
  | 'unpaid';

// ── Readings ─────────────────────────────────────────────────────────

export type Spread = 1 | 3 | 5;
export const SPREADS: readonly Spread[] = [1, 3, 5] as const;

export interface DrawnCard {
  cardId: CardId;
  /** 0-indexed position within the spread. */
  position: number;
  reversed: boolean;
  /** Free tier: the third card of a 3-card spread is withheld behind blur.webp. */
  locked?: boolean;
}

export interface Reading {
  id: string;
  deckSlug: string;
  spread: Spread;
  question: string | null;
  cards: DrawnCard[];
  /** Null for free readings, which never call the AI. */
  synthesis: string | null;
  locale: Locale;
  createdAt: string;
}
